/**
 * Every string a player can see, in UK English. scope.md §10.
 *
 * `{name}` is filled in by `translate`. `**words**` is shown in bold. A key ending in
 * `.one` / `.other` is a plural pair, chosen by `{count}`.
 *
 * The Spanish dictionary must have exactly these keys, so adding a string here without
 * translating it is a compile error.
 */

import { SERVER_ERRORS } from '../serverErrors.js';

export const en = {
  // ------------------------------------------------------------------ common
  'common.cancel': 'Cancel',
  'common.back': 'Back',
  'common.done': 'Done',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.you': 'You',
  'common.someone': 'Someone',
  'common.nobody': 'Nobody',
  'common.leave': 'Leave',
  'common.reallyLeave': 'Really leave?',
  'common.remove': 'Remove',
  'common.really': 'Really?',
  'common.howToPlay': 'How to play',
  'common.settings': 'Settings',
  'common.waitingFor': 'Waiting for {names}…',
  'common.roundOf': 'Round {round} of {rounds}',
  'seconds.one': '{count} second',
  'seconds.other': '{count} seconds',
  'seconds.short': '{count}s',

  // ------------------------------------------------------------------ connection
  'offline.message': 'No connection — trying again…',
  'offline.retry': 'Try now',

  // ------------------------------------------------------------------ home
  'home.tagline': 'Seven rounds. Lowest score wins.',
  'home.yourName': 'Your name',
  'home.namePlaceholder': 'A name, or just an initial',
  'home.nameNeeded': 'Put in a name first — an initial will do. This device remembers it.',
  'home.wins.one': '{count} game won on this device',
  'home.wins.other': '{count} games won on this device',
  'home.start': 'Start a game',
  'home.join': 'Join a game',
  'home.enterCode': 'Enter the four-letter code',
  'home.delete': 'Delete',
  'home.joinButton': 'Join',

  // ------------------------------------------------------------------ lobby
  'lobby.roomCode': 'Room code',
  'lobby.invite': 'Invite players',
  'lobby.players': 'Players ({count})',
  'lobby.offline': 'offline',
  'lobby.removePlayer': 'Remove {name}',
  'lobby.addBot': 'Add a bot',
  'lobby.rules': 'Rules',
  'lobby.change': 'Change',
  'lobby.howToPlayRules': 'How to play these rules',
  'lobby.deal': 'Deal',
  'lobby.waitingForHost': 'Waiting for the host to deal…',

  'bot.bumble.blurb': 'Easy going, makes mistakes',
  'bot.scout.blurb': 'Plays sensibly',
  'bot.ace.blurb': 'Counts cards',
  'bot.robo.blurb': 'Ruthless',

  'ruleset.house.name': 'House rules',
  'ruleset.house.description': 'The way it is played at home.',
  'ruleset.traditional.name': 'Traditional',
  'ruleset.traditional.description': 'Ace high or low, three runs of four to finish, three asks a round.',
  'ruleset.gentle.name': 'Gentle',
  'ruleset.gentle.description': 'Kinder for younger players: fifteen seconds to ask, and asking is free.',
  'ruleset.custom': 'Your own mix',

  'rules.ace': 'Ace in runs',
  'rules.ace.both': 'Either end',
  'rules.ace.low': 'Low only',
  'rules.ace.high': 'High only',
  'rules.timeToAsk': 'Time to ask',
  'rules.claimPenalty': 'Asking costs a card',
  'rules.lastRound': 'Last round',
  'rules.lastRound.allCards': 'All cards',
  'rules.lastRound.threeRunsOf4': '3 runs of 4',

  'pack.seaside.name': 'Seaside',
  'pack.space.name': 'Space Cadets',
  'pack.woodland.name': 'Woodland',
  'pack.clarity.name': 'Clarity',

  // ------------------------------------------------------------------ invite
  'invite.title': 'Invite players',
  'invite.subtitle': 'Send the link, or let someone scan this.',
  'invite.qrAlt': 'QR code for room {code}',
  'invite.share': 'Share link',
  'invite.whatsapp': 'WhatsApp',
  'invite.text': 'Text',
  'invite.copied': 'Link copied',
  'invite.shared': 'Shared',
  'invite.cancelled': 'No problem — the code is above',
  'invite.unavailable': 'Read the code out instead',
  'invite.message': 'Come and play Garbage with me — room code {code}',

  // ------------------------------------------------------------------ contracts and cards
  'contract.set.one': '{count} set of {length}',
  'contract.set.other': '{count} sets of {length}',
  'contract.run.one': '{count} run of {length}',
  'contract.run.other': '{count} runs of {length}',
  'contract.and': ' and ',
  'contract.wholeHand': '{count} runs of at least {length}, using every card',
  'meld.set': 'Set',
  'meld.run': 'Run',
  'meld.set.explained': 'a set — same number',
  'meld.run.explained': 'a run — same suit, in order',
  'card.joker': 'joker',
  'card.wild': 'WILD',

  // ------------------------------------------------------------------ briefing and dealing
  'briefing.header': 'Round {round} of {rounds} · {cards} cards each',
  'briefing.youNeed': 'You need {contract}',
  'briefing.youLead': 'You go first',
  'briefing.leads': '{name} goes first',
  'briefing.leadsTag': '(goes first)',
  'briefing.example': 'Something like this',
  'briefing.lastRound': 'Last round: your three runs must use **every card**, so there is nothing to throw away at the end.',
  'briefing.turnedUp': 'Turned up',
  'briefing.turnedUpHint': 'Anybody can ask for this once everyone is ready.',
  'briefing.yourCards': 'Your cards — tap one and use the arrows to move it',
  'briefing.ready': 'I’m ready',
  'briefing.starting': 'Starting…',
  'dealing.label': 'Dealing round {round}',
  'dealing.caption': 'Round {round} · dealing {cards} each',

  // ------------------------------------------------------------------ hand and melds
  'hand.move': 'Move',
  'hand.moveLeft': 'Move card left',
  'hand.moveRight': 'Move card right',
  'hand.empty': 'Nothing in hand.',
  'hand.count.one': '{count} card',
  'hand.count.other': '{count} cards',
  'melds.takeJoker': 'take joker',
  'melds.addHere': 'add here',
  'melds.empty': 'Nobody has laid a set or run down yet.',

  // ------------------------------------------------------------------ table
  'table.header': 'Round {round} of {rounds} · {packs} packs',
  'table.timeToAsk': 'Time to ask: {time}',
  'table.timeToAskChange': 'Time to ask: {time}. Change',
  'table.opponentDown': 'down',
  'table.opponentOff': 'off',
  'table.opponentAway': 'away',
  'table.theyGot': '{name} got the {card}',
  'table.youGot': 'You got the {card}',
  'table.extraCard': '+1 extra card',
  'table.claimTimeChanged': 'Time to ask is now {time}',
  'table.theyThrew': '{name} threw a card…',
  'table.youThrew': 'You threw a card…',
  'table.upForGrabs': 'A card is up for grabs…',
  'table.yourTurnTake': 'Your turn — take a card',
  'table.yourTurn': 'Your turn',
  'table.theirTurn': '{name}’s turn',
  'table.stock': 'Pile ({count})',
  'table.throwHere': 'Throw it here',
  'table.thrown': 'Thrown ({count})',
  'table.layDown': 'Lay down {contract}',
  'table.ways': '({count} ways)',
  'table.pendingJoker.one': 'Put the joker somewhere before you finish your turn.',
  'table.pendingJoker.other': 'Put the jokers somewhere before you finish your turn.',

  'claim.nextCardDown': 'Next card down',
  'claim.theyThrewIt': '{name} threw it',
  'claim.youThrewIt': 'You threw it',
  'claim.thrownAway': 'Thrown away',
  'claim.firstInLine': ' · first in line',
  'claim.nobodyAsked': 'Nobody has asked yet',
  'claim.mayI': 'May I?',
  'claim.costsCard': '+1 extra card',
  'claim.free': 'free — your turn, no waiting',
  'claim.asked': 'Asked ✓',
  'claim.noAsksLeft': 'No asks left',

  'dig.title': 'How many from the thrown pile?',
  'dig.hint': 'Tap a card to take everything down to it. Penalty cards come from the stock, unseen.',
  'dig.free': 'Just the top card — free',
  'dig.cost.one': '{count} cards — 1 penalty card, your hand grows by {grows}',
  'dig.cost.other': '{count} cards — {penalties} penalty cards, your hand grows by {grows}',
  'dig.costFree': '{count} cards — no penalty cards, your hand grows by {count}',
  'dig.fewer': 'One fewer',
  'dig.more': 'One more',
  'dig.take.one': 'Take {count} card',
  'dig.take.other': 'Take {count} cards',
  'dig.limit': 'That is as many as you can take',

  'dead.title': 'The round has stalled',
  'dead.why': 'The pile is empty and nothing has been laid for two laps.',
  'dead.end': 'End the round',
  'dead.addPack': 'Add a pack',
  'dead.endOnly': 'The extra pack is already in — the only way on is to end it.',
  'dead.voted': 'You chose: {choice}',
  'dead.waiting': 'Waiting for {names}…',
  'dead.rule': 'It ends only if everyone says so. If anybody wants to play on, a fresh pack is shuffled in.',

  'down.pick': 'Pick a set or run to lay down',
  'down.pickNext': 'Now pick the next one',
  'jokerEnd.question': 'Which end does the wild go?',
  'jokerEnd.low': 'Low end',
  'jokerEnd.high': 'High end',
  'jokerEnd.as': 'as {card}',
  'kick.label': 'Remove {name}',
  'kick.confirm': 'Really remove {name}?',
  'stillThere.question': 'Still there?',
  'stillThere.hint': 'Everyone is waiting. When this runs out you leave the game.',
  'stillThere.more': '+20 s',

  // ------------------------------------------------------------------ reactions
  'reactions.open': 'Send a reaction',
  'reactions.close': 'Close reactions',

  // ------------------------------------------------------------------ round and match over
  'result.matchWinner': '{name} wins!',
  'result.wentOut': '{name} went out',
  'result.ranOut': 'Round over — the cards ran out',
  'score.player': 'Player',
  'score.thisRound': 'This round',
  'score.total': 'Total',
  'score.ready': 'Ready',
  'score.notReady': 'Not ready yet',
  'score.lowestWins': 'Lowest score wins',
  'next.header': 'Next: round {round} of {rounds} · {cards} cards each',
  'roundOver.dealMeIn': 'Deal me in',
  'matchOver.playAgain': 'Play again',
  'matchOver.waitingForHost': 'Waiting for {name} to start another game…',
  'matchOver.leave': 'Leave the table',
  'matchOver.feedback': 'Tell us what you thought',

  // ------------------------------------------------------------------ settings and feedback
  'settings.title': 'Settings',
  'settings.language': 'Language',
  'settings.cardDesign': 'Card design · just for you',
  'settings.sounds': 'Sounds',
  'settings.on': 'On',
  'settings.off': 'Off',
  'settings.soundsHint': 'A chime when it is your turn or a card is on offer, and a buzz where the phone allows it.',
  'settings.feedback': 'Send feedback',
  'settings.records': 'Games are saved without any names, to help us improve the bots.',

  'feedback.title': 'Send feedback',
  'feedback.intro': 'Found something wrong, or have an idea? We read every message.',
  'feedback.kind.bug': 'Something’s wrong',
  'feedback.kind.idea': 'An idea',
  'feedback.kind.other': 'Something else',
  'feedback.message': 'What would you like to tell us?',
  'feedback.contact': 'Email, if you would like a reply (optional)',
  'feedback.private': 'Please don’t include anything private.',
  'feedback.send': 'Send',
  'feedback.sending': 'Sending…',
  'feedback.thanks': 'Thank you — it’s been sent.',
  'feedback.failed': 'That didn’t send. Please try again in a moment.',
  'feedback.tooMany': 'That’s a lot of messages — please try again later.',

  // ------------------------------------------------------------------ how to play
  'help.intro': 'These are the rules this game is using. They may differ from the way you learnt it.',
  'help.idea.title': 'The idea',
  'help.idea.body': 'Seven rounds. Each round you are dealt more cards and must build a harder combination. Cards left in your hand at the end of a round count against you, and the **lowest total after {rounds} rounds wins**.',
  'help.melds.title': 'Sets and runs',
  'help.melds.body': 'A **set** is three or more cards of the same rank. A **run** is four or more cards of the same suit in order — **four is always the minimum**.',
  'help.ace.low': 'The ace is **low only, so A-2-3-4 counts and Q-K-A does not**. A run never turns the corner through the ace, so K-A-2 is not a run.',
  'help.ace.high': 'The ace is **high only, so Q-K-A counts and A-2-3-4 does not**. A run never turns the corner through the ace, so K-A-2 is not a run.',
  'help.ace.both': 'The ace is **high or low, so A-2-3-4 and J-Q-K-A both count**. A run never turns the corner through the ace, so K-A-2 is not a run.',
  'help.jokers': 'Every pack brings **two jokers**, and a joker is wild — it stands in for whatever card you need. A set or run must contain at least one real card, but otherwise use them however you like.',
  'help.rounds.title': 'The rounds',
  'help.rounds.round': 'Round',
  'help.rounds.cards': 'Cards',
  'help.rounds.need': 'You need',
  'help.rounds.lastRound': 'In the last round the three runs use **every card in your hand**, so there is no final throw-away — laying them down is how you go out.',
  'help.turn.title': 'Your turn',
  'help.turn.body': 'Take a card — either the top of the pile or the card the last player threw away. Then lay down this round’s combination if you can, add cards to anything already on the table, and finally throw one card away.',
  'help.turn.spare': 'In rounds 1 to 6 you will always have one card spare after laying down, so you have to add it to somebody’s cards on the table before you can go out. That is normal.',
  'help.turn.onlyAdd': '**You can only add to what is already on the table.** Once you have laid your combination you cannot start a new set or run, however good it would be — three kings in your hand stay in your hand, and count against you, unless somebody has kings down.',
  'help.ask.title': '“May I?”',
  'help.ask.body': 'When someone throws a card away, everybody else gets **{time}** to ask for it. The host can make that longer or shorter at any time with the ⏱ button.',
  'help.ask.penalty': 'The player whose turn is next pays nothing — it simply becomes their card for that turn. Anybody else who asks takes an extra card from the pile as well, so their hand grows by two.',
  'help.ask.free': 'The player whose turn is next pays nothing — it simply becomes their card for that turn. In this game asking costs nothing extra.',
  'help.ask.limit': 'You may only ask **{count}** times in a round.',
  'help.ask.cascade': 'When somebody takes the thrown card **out of turn**, the card underneath it is uncovered and everybody gets another **{time}** to ask for that one — and so on down the thrown pile, until nobody wants what is showing. Only then does play carry on.',
  'help.ask.anybody': 'Anybody may ask for an uncovered card — even the player who just threw, and even a card they threw themselves on an earlier turn.',
  'help.ask.pileOnly': 'This only ever applies to the pile of thrown cards. The face-down pile stays face down, and is taken one card at a time, on your own turn.',
  'help.joker.title': 'Taking a joker',
  'help.joker.any': 'Once you have laid down, you can take a joker out of anybody’s cards on the table by giving the real card it is standing in for. You must use that joker in the same turn.',
  'help.joker.othersOnly': 'Once you have laid down, you can take a joker out of another player’s cards on the table by giving the real card it is standing in for. You must use that joker in the same turn.',
  'help.scoring.title': 'Scoring',
  'help.scoring.low': '2 to 9: **{points}** each',
  'help.scoring.ten': '10, Jack, Queen, King: **{points}** each',
  'help.scoring.ace': 'Ace: **{points}**',
  'help.scoring.joker': 'Joker: **{points}**',
  'help.bonus.finalRound': 'Going out in the last round scores minus 50.',
  'help.bonus.everyRound': 'Going out scores minus 50, in every round.',
  'help.bonus.none': 'There is no bonus for going out.',
  'help.scoring.lowest': 'Lowest total after seven rounds wins.',
  'help.gotIt': 'Got it',

  // ------------------------------------------------------------------ what the server may refuse
  'serverError.messageTooLarge': SERVER_ERRORS.messageTooLarge,
  'serverError.malformed': SERVER_ERRORS.malformed,
  'serverError.invalidMessage': SERVER_ERRORS.invalidMessage,
  'serverError.unknownMessage': SERVER_ERRORS.unknownMessage,
  'serverError.alreadyInGame': SERVER_ERRORS.alreadyInGame,
  'serverError.noSuchGame': SERVER_ERRORS.noSuchGame,
  'serverError.joinFirst': SERVER_ERRORS.joinFirst,
  'serverError.gameFull': SERVER_ERRORS.gameFull,
  'serverError.alreadyStarted': SERVER_ERRORS.alreadyStarted,
  'serverError.seatGone': SERVER_ERRORS.seatGone,
  'serverError.hostOnlyRules': SERVER_ERRORS.hostOnlyRules,
  'serverError.rulesLocked': SERVER_ERRORS.rulesLocked,
  'serverError.hostOnlyClaimTime': SERVER_ERRORS.hostOnlyClaimTime,
  'serverError.hostOnlyDeal': SERVER_ERRORS.hostOnlyDeal,
  'serverError.hostOnlyPlayAgain': SERVER_ERRORS.hostOnlyPlayAgain,
  'serverError.hostOnlyKick': SERVER_ERRORS.hostOnlyKick,
  'serverError.nothingToReadyFor': SERVER_ERRORS.nothingToReadyFor,
  'serverError.notInGame': SERVER_ERRORS.notInGame,
  'serverError.twoPlayersNeeded': SERVER_ERRORS.twoPlayersNeeded,
  'serverError.roundInProgress': SERVER_ERRORS.roundInProgress,
  'serverError.matchOver': SERVER_ERRORS.matchOver,
  'serverError.matchNotOver': SERVER_ERRORS.matchNotOver,
  'serverError.noGame': SERVER_ERRORS.noGame,
  'serverError.noClock': SERVER_ERRORS.noClock,
  'serverError.kickTooSoon': SERVER_ERRORS.kickTooSoon,
  'serverError.couldNotRemove': SERVER_ERRORS.couldNotRemove,
  'serverError.tooManyReactions': SERVER_ERRORS.tooManyReactions,
  'serverError.notAllowed': 'You can’t do that right now',
  'serverError.roomClosedEmpty': SERVER_ERRORS.roomClosedEmpty,
  'serverError.timedOut': SERVER_ERRORS.timedOut,
} as const;
